// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { chargeModeSchema } from '@/constants/charge-mode';
import {
  CHARGE_SCHEDULE,
  chargeScheduleSchema,
} from '@/constants/charge-schedule';
import { applyNullableApplicationDateRange } from '@/constants/date-shortcut';
import { INVOICE_MODE, invoiceModeSchema } from '@/constants/invoice-mode';
import { PRICING_MODE, pricingModeSchema } from '@/constants/pricing-mode';
import { Currency, refineCurrencyAmountString } from '@/currency/api/types';
import { isoCurrencyCodeSchema } from '@/currency/constants/iso';
import { toSerializableCurrency } from '@/currency/utilities/to-form-value';
import { ItemPricing } from '@/items/api/types';
import { type MilestonePhases, ProjectLineItem } from '@/project/api/types';
import { countPeriods } from '@/project/utilities/billing-frequency';
import { deriveProjectCurrency } from '@/project/utilities/project-currency';
import { dateSchema } from '@/utilities/date-time-schema';
import { DateTime } from 'luxon';
import { z } from 'zod';

export const DRAWER_WIDTH = '53.75rem';

export const FREQUENCY_OPTIONS = [
  { value: 'DAILY', label: 'Daily' },
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'BIWEEKLY', label: 'Every two weeks' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'QUARTERLY', label: 'Quarterly' },
  { value: 'SEMIANNUALLY', label: 'Semi-annually' },
  { value: 'ANNUALLY', label: 'Annually' },
  { value: 'BIENNIALLY', label: 'Every two years' },
  { value: 'TRIENNIALLY', label: 'Every three years' },
  { value: 'QUADRENNIALLY', label: 'Every four years' },
  { value: 'QUINQUENNIALLY', label: 'Every five years' },
  { value: 'ONCE', label: 'Once' },
] as const;

/**
 * Approximate months per period — only used for ordering comparison in
 * isValidFrequencyPair (charge freq ≤ invoice freq). Exact values are
 * not needed; relative ordering is all that matters.
 */
const FREQUENCY_MONTHS: Record<string, number> = {
  DAILY: 1 / 30,
  WEEKLY: 0.25,
  BIWEEKLY: 0.5,
  MONTHLY: 1,
  QUARTERLY: 3,
  SEMIANNUALLY: 6,
  ANNUALLY: 12,
  BIENNIALLY: 24,
  TRIENNIALLY: 36,
  QUADRENNIALLY: 48,
  QUINQUENNIALLY: 60,
  ONCE: Infinity,
};

/**
 * Returns true if the charge frequency can fit within the invoice frequency.
 * Charge periods must be <= invoice periods (e.g., monthly charges with quarterly invoicing is valid,
 * but quarterly charges with monthly invoicing is not).
 */
export function isValidFrequencyPair(
  chargeFrequency: string | null,
  invoiceFrequency: string | null
): boolean {
  const chargeMonths = FREQUENCY_MONTHS[chargeFrequency ?? 'MONTHLY'] ?? 1;
  const invoiceMonths = FREQUENCY_MONTHS[invoiceFrequency ?? 'MONTHLY'] ?? 1;
  return chargeMonths <= invoiceMonths;
}

/** Frequencies where the invoice amount is exact (not approximated). */
export const EXACT_INVOICE_FREQUENCIES = new Set([
  'ONCE',
  'ANNUALLY',
  'BIENNIALLY',
  'TRIENNIALLY',
  'QUADRENNIALLY',
  'QUINQUENNIALLY',
]);

const VALID_PRICING_MODES = new Set<string>(Object.values(PRICING_MODE));
const VALID_CHARGE_SCHEDULES = new Set<string>(Object.values(CHARGE_SCHEDULE));
const VALID_INVOICE_MODES = new Set<string>(Object.values(INVOICE_MODE));

export const TOGGLE_BUTTON_SX = {
  textTransform: 'none',
} as const;

export const TOGGLE_BUTTON_GROUP_SX = {
  '& .MuiToggleButton-root': {
    px: 1.5,
    textTransform: 'none',
    fontWeight: 500,
  },
} as const;

// Shared width for all inline inputs and selects in the drawer.
// Sized to fit the longest frequency label ("Every five years").
const DRAWER_INPUT_WIDTH = 180;

// Width for the billing-plan drawer's pricing, charge-schedule,
// invoice-schedule, and activation inputs — 40% wider than
// DRAWER_INPUT_WIDTH to fit currency amounts with the ISO selector.
export const BILLING_PLAN_INPUT_WIDTH = 252;

// Hide number input spinners so the $ prefix + right-aligned text is clean
export const CURRENCY_INPUT_SX = {
  width: DRAWER_INPUT_WIDTH,
  flexShrink: 0,
  '& input': { textAlign: 'right' },
  '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
    WebkitAppearance: 'none',
    margin: 0,
  },
  '& input[type=number]': {
    MozAppearance: 'textfield',
  },
} as const;

const activationDateSchema = applyNullableApplicationDateRange(
  dateSchema.nullable(),
  'Activation date'
);

// A single `price` field replaces the old unitPrice / fixedPrice pair. The
// meaning of the stored amount is derived from pricingMode at read / save
// time — PER_UNIT treats it as the per-unit rate, FIXED treats it as the
// flat amount. Storing one field means toggling the pricing mode does not
// churn the input, and the BE payload always sets exactly one of
// unit_price / fixed_price with the other explicitly null. The currency is
// carried alongside the amount (price.isoCode); the line item's single
// currency drives unit_price, fixed_price, and total_override on save.
const priceInputSchema = z
  .object({
    amount: z.string(),
    isoCode: z.preprocess(
      (v) => (typeof v === 'string' ? v.toUpperCase() : v),
      isoCurrencyCodeSchema
    ),
  })
  .superRefine(refineCurrencyAmountString);

// Form-side milestone phases. Entries carry a percentage of the line-item
// total; `mode` is retained as a single-value tag matching the API shape.
const milestonePhaseInputPercentageEntrySchema = z.object({
  name: z.string().min(1),
  percentage: z.coerce.number().min(0).max(100),
});

const milestonePhasesInputSchema = z.object({
  mode: z.literal('PERCENTAGE'),
  entries: z.array(milestonePhaseInputPercentageEntrySchema).min(1),
});

export type MilestonePhasesInput = z.infer<typeof milestonePhasesInputSchema>;

export const billingPlanFormSchema = z
  .object({
    pricingMode: pricingModeSchema,
    price: priceInputSchema,
    chargeSchedule: chargeScheduleSchema.nullable(),
    chargeFrequency: z.string().nullable(),
    invoiceMode: invoiceModeSchema.nullable(),
    invoiceFrequency: z.string().nullable(),
    totalOverride: priceInputSchema,
    milestonePhases: milestonePhasesInputSchema.nullable(),
    // Plan-level CONFIRMED / PENDING. Only meaningful for PERIODIC plans;
    // MILESTONE plans always carry CONFIRMED.
    chargeMode: chargeModeSchema,
    // Required when chargeSchedule is PERIODIC; null when MILESTONE. Defaults
    // to startOn on first save.
    activationDate: activationDateSchema,
  })
  .superRefine((values, ctx) => {
    const hasCadence =
      values.chargeFrequency !== null ||
      values.invoiceFrequency !== null ||
      values.invoiceMode !== null;

    if (values.chargeSchedule === null && hasCadence) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['chargeSchedule'],
        message: 'Select periodic before setting billing cadence',
      });
      return;
    }

    if (values.chargeSchedule === CHARGE_SCHEDULE.MILESTONE && hasCadence) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['chargeSchedule'],
        message: 'Milestone schedules cannot include periodic cadence',
      });
      return;
    }

    if (
      values.chargeSchedule === CHARGE_SCHEDULE.MILESTONE &&
      values.milestonePhases !== null
    ) {
      const phaseEntries = values.milestonePhases.entries;

      // Names that differ only by case or surrounding spaces read as one phase on a charge.
      const seenPhaseNames = new Set<string>();
      phaseEntries.forEach((entry, index) => {
        const comparedName = entry.name.trim().toLowerCase();

        if (seenPhaseNames.has(comparedName)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['milestonePhases', 'entries', index, 'name'],
            message: 'Phase names must be unique',
          });
        }

        seenPhaseNames.add(comparedName);
      });
    }

    if (values.chargeSchedule !== CHARGE_SCHEDULE.PERIODIC) {
      return;
    }

    if (values.chargeFrequency === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['chargeFrequency'],
        message: 'Charge frequency is required for periodic schedules',
      });
    }
    if (values.invoiceFrequency === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['invoiceFrequency'],
        message: 'Invoice frequency is required for periodic schedules',
      });
    }
    if (values.invoiceMode === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['invoiceMode'],
        message: 'Invoice timing is required for periodic schedules',
      });
    }
    if (
      values.chargeFrequency !== null &&
      values.invoiceFrequency !== null &&
      !isValidFrequencyPair(values.chargeFrequency, values.invoiceFrequency)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['invoiceFrequency'],
        message:
          'Invoice schedule cannot be more frequent than the charge schedule',
      });
    }
  });

export type BillingPlanFormValues = z.infer<typeof billingPlanFormSchema>;

/**
 * Counts periods by walking forward from start to end using the same
 * algorithm as generateCharges — delegates to the shared countPeriods
 * utility to ensure the count always matches the number of charges produced.
 */
export function computePeriodCount(
  startOn: DateTime | null,
  endOn: DateTime | null,
  frequency: string | null
): number {
  if (!startOn || !endOn || !frequency) return 0;
  return countPeriods(startOn, endOn, frequency);
}

/**
 * Computes the activation-shifted [start, end] range used by charge
 * generation. PERIODIC charges anchor on activationDate and run for the
 * same day-count as the line item's [startOn, endOn]. The drawer uses
 * this range for period counts so the readiness section's count
 * matches the actual generated charge list.
 */
export function computeShiftedRange(
  startOn: DateTime | null,
  endOn: DateTime | null,
  activationDate: DateTime | null
): { start: DateTime; end: DateTime } | null {
  if (!startOn || !endOn) return null;
  const start = activationDate ?? startOn;
  const durationDays = Math.round(endOn.diff(startOn, 'days').days);
  const end = start.plus({ days: durationDays });
  return {
    start,
    end,
  };
}

/**
 * Renders a line item's currency amount as the bare 2-decimal string the
 * billing-plan form holds in its `price` / `totalOverride` slots. Returns
 * null when the value is unset.
 */
function currencyAmountString(value: Currency | null): string | null {
  return value === null ? null : toSerializableCurrency(value).amount;
}

/**
 * Converts the API milestone-phases shape into the form shape. The SEED
 * boundary: API → form.
 */
function toFormMilestonePhases(
  phases: MilestonePhases | null
): MilestonePhasesInput | null {
  if (phases === null) return null;
  return {
    mode: 'PERCENTAGE',
    entries: phases.entries.map((entry) => ({
      name: entry.name,
      percentage: entry.percentage,
    })),
  };
}

export function getDefaultValues(
  lineItem: ProjectLineItem | null,
  defaultPricing: ItemPricing | null,
  defaultCurrencyCode: BillingPlanFormValues['price']['isoCode']
): BillingPlanFormValues {
  const rawPricingMode =
    lineItem?.pricingMode ?? defaultPricing?.pricingMode ?? null;
  const rawChargeSchedule =
    lineItem?.chargeSchedule ?? defaultPricing?.chargeSchedule ?? null;
  const rawInvoiceMode =
    lineItem?.invoiceMode ?? defaultPricing?.invoiceMode ?? null;

  const chargeSchedule: BillingPlanFormValues['chargeSchedule'] =
    rawChargeSchedule !== null && VALID_CHARGE_SCHEDULES.has(rawChargeSchedule)
      ? (rawChargeSchedule as BillingPlanFormValues['chargeSchedule'])
      : CHARGE_SCHEDULE.PERIODIC;

  // Milestone plans don't carry a charge or invoice schedule — the BE
  // stores null for all three. The drawer's schedule-change effect
  // enforces the same invariant whenever the user flips to milestone,
  // so `getDefaultValues` must return the same shape on the initial
  // render or the form will appear tainted without the user touching
  // anything (form.chargeFrequency = null via the effect, but the
  // default-derived initialFormValues would still hold 'MONTHLY').
  const isMilestone = chargeSchedule === CHARGE_SCHEDULE.MILESTONE;

  const pricingMode =
    rawPricingMode !== null && VALID_PRICING_MODES.has(rawPricingMode)
      ? (rawPricingMode as BillingPlanFormValues['pricingMode'])
      : PRICING_MODE.PER_UNIT;
  const defaultPrice =
    defaultPricing?.pricingMode === pricingMode
      ? pricingMode === PRICING_MODE.PER_UNIT
        ? defaultPricing.unitPrice
        : defaultPricing.fixedPrice
      : null;
  // Seed the single price field from whichever stored column matches the
  // current pricing mode. Falls back to the other column so legacy rows that
  // carry both don't surface as empty when opened.
  const seededLineItemPrice =
    pricingMode === PRICING_MODE.PER_UNIT
      ? (lineItem?.unitPrice ?? lineItem?.fixedPrice ?? null)
      : (lineItem?.fixedPrice ?? lineItem?.unitPrice ?? null);
  // The line item's single currency drives the price field; derive it from
  // whichever currency-bearing column is set, falling back to the org
  // default so a brand-new plan opens with a usable currency selector.
  const seededCurrencyCode =
    deriveProjectCurrency([
      lineItem?.unitPrice ?? null,
      lineItem?.fixedPrice ?? null,
      lineItem?.totalOverride ?? null,
    ]) ?? defaultCurrencyCode;
  // Seed the amount from the line item if it has one. Otherwise borrow the SKU
  // catalog default ONLY when it shares the project currency — we never
  // implicitly convert a foreign-currency default into the project currency,
  // so a USD default on a CAD project opens at 0 CAD (the default is surfaced
  // as a reference label instead).
  const matchingDefaultPrice =
    defaultPrice !== null && defaultPrice.isoCode === seededCurrencyCode
      ? defaultPrice
      : null;
  const seededPriceAmount =
    currencyAmountString(seededLineItemPrice) ??
    currencyAmountString(matchingDefaultPrice) ??
    '';
  const seededActivationDate = lineItem?.activationDate ?? lineItem?.startOn;

  return {
    pricingMode,
    price: { amount: seededPriceAmount, isoCode: seededCurrencyCode },
    chargeSchedule,
    chargeFrequency: isMilestone
      ? null
      : (lineItem?.chargeFrequency?.toUpperCase() ??
        defaultPricing?.chargeFrequency?.toUpperCase() ??
        'MONTHLY'),
    invoiceMode: isMilestone
      ? null
      : rawInvoiceMode !== null && VALID_INVOICE_MODES.has(rawInvoiceMode)
        ? (rawInvoiceMode as BillingPlanFormValues['invoiceMode'])
        : INVOICE_MODE.ADVANCE,
    invoiceFrequency: isMilestone
      ? null
      : (lineItem?.invoiceFrequency?.toUpperCase() ??
        defaultPricing?.invoiceFrequency?.toUpperCase() ??
        'MONTHLY'),
    totalOverride: {
      amount: currencyAmountString(lineItem?.totalOverride ?? null) ?? '',
      isoCode: seededCurrencyCode,
    },
    milestonePhases: toFormMilestonePhases(lineItem?.milestonePhases ?? null),
    // ChargeMode is non-nullable on the response schema, so the only
    // path that hits this fallback is a brand-new plan with no
    // lineItem yet (the form initializes with fresh-plan defaults).
    chargeMode: lineItem?.chargeMode ?? 'CONFIRMED',
    // For PERIODIC plans, seed activationDate from start_on when the BE
    // hasn't stamped one yet (first-save case). The picker shows this
    // as the displayed default; build-line-item-update-payload diffs
    // against the same fallback so an untouched field doesn't generate
    // a spurious audit. MILESTONE plans carry null (dateless schedule).
    activationDate:
      isMilestone || seededActivationDate == null ? null : seededActivationDate,
  };
}

/**
 * Default milestone phases seeded into a fresh plan. Five canonical HaaS
 * phases (contract through activation) with a weighted distribution
 * summing to 100%: contract is the largest share, installation next,
 * shipment / delivery / activation filling in the remainder. Users can
 * rename, reorder, or rebalance as needed.
 */
const DEFAULT_MILESTONE_PHASES: { name: string; percentage: number }[] = [
  { name: 'Contract signed', percentage: 40 },
  { name: 'Shipment', percentage: 10 },
  { name: 'Delivery', percentage: 10 },
  { name: 'Installation', percentage: 30 },
  { name: 'Activation', percentage: 10 },
];

/**
 * Builds the canonical seed phases blob for a plan that has just switched
 * into milestone mode. Pre-distributes to sum to 100% so the user sees a
 * ready-to-generate plan from the first render.
 */
export function buildEmptyMilestonePhases(): MilestonePhasesInput {
  return {
    mode: 'PERCENTAGE',
    entries: DEFAULT_MILESTONE_PHASES.map(({ name, percentage }) => ({
      name,
      percentage,
    })),
  };
}

/**
 * Returns null when milestone phases are ready to generate charges from,
 * or a human-readable reason string when generation should be blocked.
 * The caller uses the string as a tooltip on a disabled Generate button.
 *
 * Requires the sum of all entry percentages to equal 100 (±0.01 for decimal
 * rounding); a mismatch means the charges would either under- or over-bill
 * the line-item total, which the user almost certainly didn't intend.
 */
export function milestoneGenerateBlockedReason(
  phases: MilestonePhasesInput | null
): string | null {
  if (phases === null) {
    return 'Add milestone phases before generating charges';
  }
  if (phases.entries.length === 0) {
    return 'Add milestone phases before generating charges';
  }
  const sum = phases.entries.reduce((acc, entry) => acc + entry.percentage, 0);
  if (Math.abs(sum - 100) > 0.01) {
    return `Phases must sum to 100% to generate charges (currently ${sum.toFixed(2)}%)`;
  }
  return null;
}
